// app/javascript/controllers/image_viewer_controller.js
import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "main",
        "slide",
        "thumb",
        "thumbs",
        "counter",
        "totalCounter",
        "counterWrapper",
        "thumbsWrapper",
        "navPrev",
        "navNext",
        "actionsBar",
        "infoBar",
        "video"
    ]

    static values = {
        viewerId: String,
        total: Number,
        mode: String, // "gallery" or "profile"
        openMediaId: String, // Deep link: media to open on load (e.g. from a comment notification)
        openCommentsUrl: String // Deep link: comments drawer to open alongside that media
    }

    connect() {
        this.currentIndex = 0
        this.touchStartX = 0
        this.touchEndX = 0
        this.touchStartY = 0
        this.touchEndY = 0
        this.isSwiping = false

        // Bind event handlers
        this.boundOpenHandler = this.handleOpenEvent.bind(this)
        this.boundTouchStart = this.handleTouchStart.bind(this)
        this.boundTouchEnd = this.handleTouchEnd.bind(this)
        this.boundKeydown = this.handleKeydown.bind(this)

        // Listen for custom open event
        this.element.addEventListener("open-image-viewer", this.boundOpenHandler)

        // Sync total from actual DOM targets
        this._refreshTotal()

        // Ensure initial UI state is consistent (thumb highlight, actions, drawers)
        this.updateUiForIndex(0)

        // Setup touch events for swipe
        if (this.hasMainTarget) {
            this.mainTarget.addEventListener("touchstart", this.boundTouchStart, {passive: true})
            this.mainTarget.addEventListener("touchend", this.boundTouchEnd, {passive: true})
        }

        // If the viewer was moved to body for opening (via open()), finish the open sequence.
        // This fires on the NEW Stimulus instance created after the DOM move.
        if (this.element.hasAttribute("data-pending-open")) {
            const pendingIndex = parseInt(this.element.getAttribute("data-pending-open"), 10) || 0
            this.element.removeAttribute("data-pending-open")
            this._finishOpen(pendingIndex)
        } else if (this.openMediaIdValue) {
            this._openDeepLinkedMedia()
        }
    }

    disconnect() {
        document.removeEventListener("keydown", this.boundKeydown)

        if (this.hasMainTarget) {
            this.mainTarget.removeEventListener("touchstart", this.boundTouchStart)
            this.mainTarget.removeEventListener("touchend", this.boundTouchEnd)
        }

        if (this.boundOpenHandler) {
            this.element.removeEventListener("open-image-viewer", this.boundOpenHandler)
        }
    }

    // ─── Deep link ──────────────────────────────────────────────────────

    // Opens the viewer on the deep-linked media and its comments drawer, so a comment
    // made on a single media of a post/event gallery is visible right away.
    _openDeepLinkedMedia() {
        const mediaId = this.openMediaIdValue
        const commentsUrl = this.openCommentsUrlValue

        // Consume the deep link: open() moves the viewer to <body>, which reconnects
        // the controller, and closing/reopening must not replay it.
        this.element.removeAttribute("data-image-viewer-open-media-id-value")
        this.element.removeAttribute("data-image-viewer-open-comments-url-value")

        const index = this.slideTargets.findIndex(slide => slide.dataset.imageId === String(mediaId))
        if (index < 0) return

        this.open(index)

        if (commentsUrl) {
            // Let the viewer finish opening (and the global drawer connect) first.
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent("global-drawer:open", {
                    bubbles: true,
                    detail: {src: commentsUrl}
                }))
            }, 300)
        }
    }

    // ─── Target lifecycle callbacks (Turbo Stream prepend/remove sync) ───

    slideTargetConnected(_element) {
        this._refreshTotal()
    }

    slideTargetDisconnected(_element) {
        this._refreshTotal()
        // Clamp current index if a slide was removed
        if (this.currentIndex >= this.totalValue) {
            this.currentIndex = Math.max(0, this.totalValue - 1)
        }
        // Update UI only if the viewer is currently open
        if (this.element.classList.contains("open")) {
            this.updateUiForIndex(this.currentIndex)
        }
    }

    // ─── Event handlers ─────────────────────────────────────────────────

    handleOpenEvent(event) {
        const {index = 0, mediaId} = event.detail || {}

        if (mediaId) {
            // Find the slide by media ID (used in album mode for stable navigation)
            const slideIndex = this.slideTargets.findIndex(
                slide => slide.dataset.imageId === String(mediaId)
            )
            this.open(slideIndex >= 0 ? slideIndex : 0)
        } else {
            this.open(index)
        }
    }

    handleTouchStart(event) {
        this.touchStartX = event.changedTouches[0].screenX
        this.touchStartY = event.changedTouches[0].screenY
    }

    handleTouchEnd(event) {
        this.touchEndX = event.changedTouches[0].screenX
        this.touchEndY = event.changedTouches[0].screenY
        this.handleSwipe()
    }

    handleSwipe() {
        if (this.isSwiping) return

        const threshold = 50
        const diffX = this.touchStartX - this.touchEndX
        const diffY = Math.abs(this.touchStartY - this.touchEndY)

        // Only trigger horizontal swipe if movement is primarily horizontal
        if (Math.abs(diffX) > threshold && Math.abs(diffX) > diffY) {
            this.isSwiping = true

            if (diffX > 0) {
                this.next()
            } else {
                this.prev()
            }

            setTimeout(() => {
                this.isSwiping = false
            }, 300)
        }
    }

    handleKeydown(event) {
        switch (event.key) {
            case "ArrowLeft":
                this.prev()
                break
            case "ArrowRight":
                this.next()
                break
            case "Escape":
                this.close()
                break
        }
    }

    // ─── Viewer actions ─────────────────────────────────────────────────

    open(index = 0) {
        const overlay = this.element

        if (overlay.parentElement !== document.body) {
            // Store the target slide index so the NEW Stimulus instance (created after
            // the DOM move) can finish opening via connect() → _finishOpen().
            overlay.setAttribute("data-pending-open", String(index))
            overlay._originalParent = overlay.parentElement
            overlay._originalNextSibling = overlay.nextSibling
            document.body.appendChild(overlay)
            // Stimulus will disconnect this instance and connect a new one.
            // The new instance's connect() detects data-pending-open and calls _finishOpen().
        } else {
            // Already in body (no DOM move needed), open directly
            this._finishOpen(index)
        }
    }

    /**
     * Complete the viewer open sequence. Called either directly (no DOM move)
     * or from connect() after the element has been moved to body.
     */
    _finishOpen(index) {
        // Use requestAnimationFrame so the DOM is fully settled before showing
        requestAnimationFrame(() => {
            this.goToSlide(index)
            this.updateUiForIndex(this.currentIndex)

            this.element.classList.remove("hidden")
            this.element.classList.add("open")
            this.element.setAttribute("aria-hidden", "false")

            document.addEventListener("keydown", this.boundKeydown)
            this.pauseAllVideos()
        })
    }

    close() {
        // Pause all videos
        this.pauseAllVideos()

        const overlay = this.element

        // Don't use HSOverlay.close() as it may cascade to parent modals
        // Instead, manually hide the overlay
        overlay.classList.add("hidden")
        overlay.classList.remove("open", "hs-overlay-open:opacity-100")
        overlay.setAttribute("aria-hidden", "true")

        document.removeEventListener("keydown", this.boundKeydown)

        // Move back to original position after the hide animation
        // This triggers a Stimulus disconnect/reconnect which resets the controller
        setTimeout(() => {
            if (overlay._originalParent && overlay.parentElement === document.body) {
                if (overlay._originalNextSibling) {
                    overlay._originalParent.insertBefore(overlay, overlay._originalNextSibling)
                } else {
                    overlay._originalParent.appendChild(overlay)
                }
                overlay._originalParent = null
                overlay._originalNextSibling = null
            }
        }, 300)
    }

    next() {
        if (this.currentIndex < this.totalValue - 1) {
            this.goToSlide(this.currentIndex + 1)
        }
    }

    prev() {
        if (this.currentIndex > 0) {
            this.goToSlide(this.currentIndex - 1)
        }
    }

    goTo(event) {
        // Support media-id based navigation (for dynamically added thumbs)
        const mediaId = event.currentTarget.dataset.mediaId
        if (mediaId) {
            const slideIndex = this.slideTargets.findIndex(
                slide => slide.dataset.imageId === String(mediaId)
            )
            if (slideIndex >= 0) {
                this.goToSlide(slideIndex)
                return
            }
        }
        // Fallback to index-based navigation
        const index = parseInt(event.currentTarget.dataset.index, 10)
        this.goToSlide(index)
    }

    goToSlide(index) {
        const clamped = Math.max(0, Math.min(index, this.totalValue - 1))

        // Pause all videos when switching
        this.pauseAllVideos()

        this.currentIndex = clamped
        this.updateUiForIndex(clamped)
    }

    pauseAllVideos() {
        // Pause videos with target attribute
        if (this.hasVideoTarget) {
            this.videoTargets.forEach(video => {
                try {
                    video.pause()
                    video.currentTime = 0
                } catch (_) {
                }
            })
        }
        // Also pause any video elements in the viewer (fallback)
        this.element.querySelectorAll("video").forEach(video => {
            try {
                video.pause()
            } catch (_) {
            }
        })
    }

    // ─── UI update ──────────────────────────────────────────────────────

    updateUiForIndex(index) {
        // Update slides
        this.slideTargets.forEach((slide, i) => {
            if (i === index) {
                slide.classList.remove("hidden")
                slide.classList.add("flex")
            } else {
                slide.classList.add("hidden")
                slide.classList.remove("flex")
            }
        })

        // Update thumbnails
        // Use Stimulus targets if available, otherwise query DOM directly as fallback
        let thumbElements = []
        if (this.hasThumbTarget) {
            thumbElements = this.thumbTargets
        } else {
            // Fallback: query thumbnails directly from the element
            const thumbsContainer = this.element.querySelector('[data-image-viewer-target="thumbs"]')
            if (thumbsContainer) {
                thumbElements = Array.from(thumbsContainer.querySelectorAll('[data-image-viewer-target="thumb"]'))
            }
        }

        if (thumbElements.length > 0) {
            thumbElements.forEach((thumb, i) => {
                if (i === index) {
                    thumb.classList.add("border-white", "ring-offset-2", "ring-offset-black/60")
                    thumb.classList.remove("border-transparent")
                } else {
                    thumb.classList.remove("border-white", "ring-offset-2", "ring-offset-black/60")
                    thumb.classList.add("border-transparent")
                }
            })
        }

        // Update actions bar (gallery mode or profile with report button)
        if (this.hasActionsBarTarget) {
            this.actionsBarTargets.forEach((bar, i) => {
                if (i === index) {
                    bar.classList.remove("hidden")
                } else {
                    bar.classList.add("hidden")
                }
            })
        }

        // Update info bar (description + tagged users - gallery mode only)
        // Use Stimulus targets if available, otherwise query DOM directly as fallback
        let infoBarElements = []
        if (this.hasInfoBarTarget) {
            infoBarElements = this.infoBarTargets
        } else {
            // Fallback: query info bars directly from the element
            infoBarElements = Array.from(this.element.querySelectorAll('[data-image-viewer-target="infoBar"]'))
        }

        if (infoBarElements.length > 0) {
            infoBarElements.forEach((bar, i) => {
                if (i === index) {
                    bar.classList.remove("hidden")
                } else {
                    bar.classList.add("hidden")
                }
            })
        }

        // Update counter
        if (this.hasCounterTarget) {
            this.counterTarget.textContent = index + 1
        }
    }

    // ─── Internal: sync total from DOM ──────────────────────────────────

    _refreshTotal() {
        const newTotal = this.slideTargets.length
        this.totalValue = newTotal

        // Update the total counter text
        if (this.hasTotalCounterTarget) {
            this.totalCounterTarget.textContent = newTotal
        }

        // Show/hide counter wrapper (visible when > 1)
        if (this.hasCounterWrapperTarget) {
            if (newTotal > 1) {
                this.counterWrapperTarget.classList.remove("hidden")
            } else {
                this.counterWrapperTarget.classList.add("hidden")
            }
        }

        // Show/hide thumbnails wrapper (visible when > 1)
        if (this.hasThumbsWrapperTarget) {
            if (newTotal > 1) {
                this.thumbsWrapperTarget.classList.remove("hidden")
            } else {
                this.thumbsWrapperTarget.classList.add("hidden")
            }
        }

        // Show/hide navigation arrows (visible on sm+ when > 1)
        if (this.hasNavPrevTarget) {
            if (newTotal > 1) {
                this.navPrevTarget.classList.remove("hidden")
                this.navPrevTarget.classList.add("sm:flex")
            } else {
                this.navPrevTarget.classList.add("hidden")
                this.navPrevTarget.classList.remove("sm:flex")
            }
        }
        if (this.hasNavNextTarget) {
            if (newTotal > 1) {
                this.navNextTarget.classList.remove("hidden")
                this.navNextTarget.classList.add("sm:flex")
            } else {
                this.navNextTarget.classList.add("hidden")
                this.navNextTarget.classList.remove("sm:flex")
            }
        }
    }

    // ─── Gallery mode actions ───────────────────────────────────────────

    toggleLike(event) {
        if (this.modeValue !== "gallery") return

        const button = event.currentTarget
        const imageId = this.slideTargets[this.currentIndex].dataset.imageId
        const likeableType = event.params.likeableType || "Media"
        const isLiked = button.dataset.liked === "true"

        // Toggle UI immediately
        const svg = button.querySelector("svg")
        const countSpan = button.querySelector("span")

        if (isLiked) {
            svg.classList.remove("fill-red-500", "stroke-red-500")
            svg.classList.add("fill-none", "stroke-current")
            button.dataset.liked = "false"
            if (countSpan) {
                countSpan.textContent = Math.max(0, parseInt(countSpan.textContent) - 1)
            }
        } else {
            svg.classList.add("fill-red-500", "stroke-red-500")
            svg.classList.remove("fill-none", "stroke-current")
            button.dataset.liked = "true"
            if (countSpan) {
                countSpan.textContent = parseInt(countSpan.textContent) + 1
            }
        }

        // Send request to server using polymorphic route
        const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
        const method = isLiked ? "DELETE" : "POST"
        const url = `/likes/${likeableType}/${imageId}`

        fetch(url, {
            method: method,
            headers: {
                "X-CSRF-Token": csrfToken,
                "Content-Type": "application/json",
                "Accept": "text/vnd.turbo-stream.html"
            }
        }).catch(error => {
            console.error("Error toggling like:", error)
            // Revert UI on error
            if (!isLiked) {
                svg.classList.remove("fill-red-500", "stroke-red-500")
                svg.classList.add("fill-none", "stroke-current")
                button.dataset.liked = "false"
                if (countSpan) {
                    countSpan.textContent = Math.max(0, parseInt(countSpan.textContent) - 1)
                }
            } else {
                svg.classList.add("fill-red-500", "stroke-red-500")
                svg.classList.remove("fill-none", "stroke-current")
                button.dataset.liked = "true"
                if (countSpan) {
                    countSpan.textContent = parseInt(countSpan.textContent) + 1
                }
            }
        })
    }

}
